import type { ReactNode } from 'react'

interface SectionHeadingProps {
  title: ReactNode
  subtitle?: ReactNode
  align?: 'left' | 'center'
  /** Render as <h1> instead of <h2> — use ONLY when this is a routed page's
   *  single top-level heading (technical SEO pass: every routed page needs
   *  exactly one real <h1>; a page with no <h1> at all, or one buried under
   *  a mid-page <h2>, was the actual bug this prop fixes). Defaults to 'h2'
   *  since most call sites are mid-page section headers, not page titles. */
  as?: 'h1' | 'h2'
}

/** Serif title + sans subtitle, matching the Stitch section-header pattern used across all pages.
 *  No eyebrow/kicker label above the heading — removed per the design audit (impeccable craft-floor
 *  ban: a kicker above an <h2> earns nothing back, the heading carries its own weight). */
export function SectionHeading({ title, subtitle, align = 'center', as: Heading = 'h2' }: SectionHeadingProps) {
  const alignClasses = align === 'center' ? 'text-center mx-auto' : 'text-left'

  return (
    <div className={`flex flex-col gap-3 max-w-2xl ${alignClasses}`}>
      <Heading className="font-serif text-[clamp(1.75rem,4vw,3rem)] font-semibold leading-tight text-foreground">
        {title}
      </Heading>
      {subtitle && (
        <p className="text-foreground-muted text-base md:text-lg leading-relaxed">{subtitle}</p>
      )}
    </div>
  )
}
